import { RootState } from "state/store";
import { api } from "./api";

interface TestStatus {
    CoGuid: string;
    progress: "NotStarted" | "InProgress" | "Complete" | string;
}

interface ContentResponse {
    Description: string;
    TextData: string;
    ContentItemTypeId: number;
    IsDeletable: boolean;
    PublishDate: string;
    Version: number;
    ContentItemId: number;
    InsertBy: string;
    InsertDate: string;
    AltTag: string | null;
    ImageData: string | null;
    NotificationTitle: string | null;
    NotificationMetadata: string | null;
    IsProtected: boolean;
    FullyLoaded: boolean;
}

export interface AlternateSegment {
    ProductOfferingSegmentId: number | null;
    ProductOfferingSegmentCode: string;
    ProductOfferingId: number;
    Description: string;
    Starttime: string;
    Endtime: string;
    StarttimeUTC: string;
    EndtimeUTC: string;
    InstructorMsmId: number;
    InstructorName: string;
    InstructorEmail: string;
    InstructorPhotoUrl: string;
    ClassUrl: string;
}

export const homeApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getOverallProgress: builder.query<any, { Userid: string, Productcode: string, Offeringcode: number }>({
            query: (args) => {
                const { Userid, Productcode, Offeringcode } = args;
                return {
                    url: "/Home/OverallProgress",
                    method: "GET",
                    params: {
                        Userid: Userid,
                        Productcode: Productcode,
                        Offeringcode: Offeringcode
                    },
                }
            },
            providesTags: ["OverallProgress"]
        }),
        getPerformanceByTopic: builder.query<any, void>({
            query: () => ({
                url: "/Home/PerformanceByTopic",
                method: "GET",
            })
        }),
        getTestScoresAndPerformance: builder.query<any, void>({
            query: () => ({
                url: "/Home/TestScoresAndPerformance",
                method: "GET",
            })
        }),
        getPerformanceByTest: builder.query<any, void>({
            query: () => ({
                url: "/Home/PerformanceByTest",
                method: "GET",
            })
        }),
        getCoursework: builder.query<any, { page?: number, selectedOption?: number, timeFilter?: number, subject?: number }>({
            query: (arg) => {
                const { page, selectedOption, timeFilter, subject } = arg;
                return {
                    url: "/Home/Coursework",
                    params: {
                        page: page,
                        ...selectedOption && { filter: selectedOption - 1 },
                        ...(subject !== undefined || subject !== null) && { subjectFilter: subject },
                        timeFilter: timeFilter
                    },
                    method: "GET",
                }
            },
            providesTags: ["Coursework"]
        }),
        updateStatus: builder.mutation<any, TestStatus>({
            query: (body) => ({
                url: "/Home/UpdateStatus",
                body: body,
                method: "PUT",
            }),
            invalidatesTags: ["Home", "CourseIndex"]
        }),
        viewPrepInfo: builder.query<any, void>({
            query: () => {
                return {
                    url: "/Home/ViewPrepInfo",
                    method: "GET",
                }
            }
        }),
        addFeedback: builder.mutation<any, { ProductOfferingSegmentId: number, Rating: number, FeedbackText: string }>({
            query: (body) => ({
                url: "/Home/AddFeedback",
                body: body,
                method: "PUT"
            })
        }),
        getContent: builder.query<ContentResponse, { contentkey: string }>({
            query: (args) => {
                const { contentkey } = args;
                return {
                    url: "/Home/GetContent",
                    method: "GET",
                    params: {
                        contentkey: contentkey
                    },
                }
            },
        }),
        getGMACMaterials: builder.query<{ Materials: Array<{ Name: string, Pin: string, Order: number }> }, void>({
            query: () => ({
                url: "/Materials/GetGMACMaterials",
                method: "GET",
            })
        }),
        getAlternateSessions: builder.query<AlternateSegment[], { segmentCode: string }>({
            query: (args) => {
                const { segmentCode } = args;
                return {
                    url: "/Home/AlternateSessions",
                    method: "GET",
                    params: {
                        segmentCode: segmentCode
                    },
                }
            },
        })
    })
})

export const {
    useGetOverallProgressQuery,
    useGetPerformanceByTopicQuery,
    useGetTestScoresAndPerformanceQuery,
    useGetPerformanceByTestQuery,
    useGetCourseworkQuery,
    useLazyGetCourseworkQuery,
    useUpdateStatusMutation,
    useViewPrepInfoQuery,
    useAddFeedbackMutation,
    useGetContentQuery,
    useLazyGetContentQuery,
    useGetGMACMaterialsQuery,
    useGetAlternateSessionsQuery,
    useLazyGetAlternateSessionsQuery
} = homeApi;